import React, { useState, useRef } from "react";
import {
  Shield,
  Zap,
  CheckCircle,
  ArrowRight,
  Building2,
  Lock,
  Users,
  BarChart3,
  FileText,
  Globe,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const LoginPage = () => {
  const { login, isLoading } = useAuth();
  const [isHovered, setIsHovered] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const loginAttemptRef = useRef(false);

  const handleLogin = async () => {
    // ✅ Prevent multiple simultaneous login attempts
    if (isLoggingIn || loginAttemptRef.current) {
      console.warn("Login already in progress, ignoring duplicate click");
      return;
    }

    // ✅ Safari-specific: Check if recent login attempt was made (within 5 seconds)
    try {
      const lastAttempt = sessionStorage.getItem("login_attempt_time");
      if (lastAttempt) {
        const timeSinceAttempt = Date.now() - parseInt(lastAttempt, 10);
        if (timeSinceAttempt < 5000) {
          console.warn(
            `Safari: Recent login attempt detected ${timeSinceAttempt}ms ago, aborting`,
          );
          return;
        }
      }
    } catch (e) {
      console.warn("Failed to check login attempt time:", e);
    }

    loginAttemptRef.current = true;
    setIsLoggingIn(true);

    try {
      await login();
    } catch (error) {
      // Error is already logged in AuthContext
      console.error("Login handler error:", error);
    } finally {
      // Keep the flag set for longer to prevent rapid re-attempts
      setTimeout(() => {
        loginAttemptRef.current = false;
        setIsLoggingIn(false);
      }, 3000);
    }
  };

  const features = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Enterprise Security",
      description: "Compliant with end-to-end encryption",
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "AI-Powered Analysis",
      description: "Advanced document processing and data insights",
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "File Processing",
      description: "Upload and analyze documents of any type securely",
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Business Intelligence",
      description: "Get actionable insights from your enterprise data",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex">
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-lg">
          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <img
                src="/KPMG.svg"
                alt="Company Logo"
                className="h-10 w-auto transition-all duration-200"
              />

              <div className="flex justify-center mb-6">
                <div className="relative">
                  <img
                    src="/logo_new.png"
                    alt="KPMG"
                    className="w-16 h-16 object-contain"
                  />
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
                </div>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Gordon - Dev
              </h1>
              <p className="text-lg text-gray-600">
                Secure access to your AI-powered platform intelligence
              </p>
            </div>

            {/* Login Card */}
            <div className="space-y-6">
              {/* Azure AD Login Button */}
              <button
                onClick={handleLogin}
                disabled={isLoading || isLoggingIn}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className="w-full group relative overflow-hidden bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 text-white rounded-2xl p-5 font-semibold text-lg transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed mb-8"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative flex items-center justify-center space-x-3">
                  {isLoading || isLoggingIn ? (
                    <>
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Authenticating...</span>
                    </>
                  ) : (
                    <>
                      <Building2 className="w-6 h-6" />
                      <span>Sign in with SSO</span>
                      <ArrowRight
                        className={`w-6 h-6 transition-transform duration-300 ${
                          isHovered ? "translate-x-1" : ""
                        }`}
                      />
                    </>
                  )}
                </div>
              </button>

              {/* Security Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Lock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-900 mb-1">
                      Secure Authentication
                    </p>
                    <p className="text-blue-700">
                      Your organization's Azure Active Directory credentials are
                      used for secure, single sign-on access to the platform.
                    </p>
                  </div>
                </div>
              </div>

              {/* Features Preview */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  What you'll get access to:
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-start space-x-2 p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="text-blue-600 mt-0.5">{feature.icon}</div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate">
                          {feature.title}
                        </p>
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="mt-8 text-center">
                <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
                  <div className="flex items-center space-x-1">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Security Compliant</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Globe className="w-4 h-4 text-blue-500" />
                    <span>Global Infrastructure</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Users className="w-4 h-4 text-purple-500" />
                    <span>Enterprise Ready</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
